import { Input as DesignInput, Text } from "@solomon-labs/design/components/ui";
import type { JSX } from "react";
import React, { useEffect, useRef, useState } from "react";

import { useInputCaps } from "../hooks/useInputCaps";
import { sanitizeDecimal } from "../utils/sanitizeDecimal";

import { InputFeedback } from "./InputFeedback";
import { InputUnderline } from "./InputUnderline";
import { SeekBar } from "./SeekBar";

/**
 * Display mode for AmountInput.
 *
 * - `"input"` — Full interactive mode with editable input, seekbar, and meta row.
 * - `"display"` — Read-only with all chrome visible (label, value, chip, divider, meta).
 * - `"compact"` — Stripped to label + value + token icon only. Divider, meta row,
 *   and CutFrame borders fade out with CSS transitions. Use for confirmed transaction
 *   states where only the amount matters.
 */
type AmountInputMode = "input" | "display" | "compact";

interface AmountInputProps {
    valueVariant?: "number" | "number-lg";
    fit?: boolean;
    /** Field label rendered above the input (e.g., "You Send"). */
    label: React.ReactNode;
    /** Current input value. */
    value: string;
    /** Change handler — already-sanitized numeric string. */
    onChange?: (next: string) => void;
    /** Optional ref forwarded to the underlying <input>. */
    inputRef?: React.RefObject<HTMLInputElement | null>;
    /** Optional fixed prefix (e.g., "$"). Rendered with the same numeric font as the input. */
    prefix?: string;
    /** Token chip displayed on the right of the input. Caller can wrap with
     *  CutFrame if the swap-style cut-corner border is desired. */
    tokenChip?: React.ReactNode;
    /** Optional placeholder. Defaults to "0". */
    placeholder?: string;
    /** Focus / blur handlers. */
    onFocus?: () => void;
    onBlur?: () => void;
    /** Balance string shown on the right of the meta row (e.g., "$100,000.00"). */
    balance?: string;
    /** Called when the balance text is clicked. Use for exact "max" fills. */
    onBalanceClick?: () => void;
    /** Secondary value shown on the left of the meta row. In swap mode this is
     *  the dollar conversion (e.g., "$1,234.00"); in stake mode it's the
     *  receive amount (e.g., "0 sUSDv"). The component fades it in based on
     *  whether the value is non-empty. */
    secondary?: React.ReactNode;
    /** Show SeekBar with ticks. Tick visibility tracks the input's
     *  internal focus state. */
    seekable?: boolean;
    /** SeekBar fill percentage (0-100). */
    pct?: number;
    /** Called when user clicks a tick on the SeekBar. */
    onSeek?: (pct: number) => void;
    /**
     * Display mode. Defaults to `"input"`.
     *
     * - `"input"` — fully interactive editable input.
     * - `"display"` — non-editable, all chrome (label, value, chip, divider,
     *   meta row) still rendered. Use for read-only fields like "You Get" in
     *   swap.
     * - `"compact"` — label + value + token icon only. Divider, meta row, and
     *   CutFrame decorations transition out. Use for confirmed-transaction
     *   states where only the amount matters.
     *
     * `"display"` and `"compact"` both make the underlying input non-editable.
     */
    mode?: AmountInputMode;
    /** Draw an animated charcoal border around the input row on focus. Uses stake-* keyframes. */
    focusBorder?: boolean;
    /**
     * Animate the meta row (secondary + balance) closed. Default `false` →
     * meta is visible. Set to `true` to collapse to 0 height with a transition.
     * Toggle from parent state (e.g., `!isInputting && !hasInput`) for landing
     * widget UX where balances only appear after the user engages.
     */
    metaCollapsed?: boolean;
    /**
     * Pulses the entire input area (value, chip, baseline) via Tailwind's
     * `animate-pulse` to indicate the displayed value is being computed —
     * e.g., a quote fetch in-flight on a `mode="display"` "You Get" field.
     */
    loading?: boolean;
    /**
     * Maximum number of digits allowed AFTER the decimal point. Pass the
     * active token's decimals (e.g., USDC=6, USDv=9) so users can't type
     * below the token's smallest representable unit. Rejection triggers a
     * shake + warning-yellow flash.
     *
     * Resolved via `useInputCaps` — see that hook for the default fallback.
     */
    maxDecimals?: number;
    /**
     * Maximum number of digits allowed BEFORE the decimal point. Resolved
     * via `useInputCaps` — defaults to 15 (≈ $1Q ceiling, past where number
     * formatters switch to scientific notation). Rejection fires the same
     * shake + warning-yellow flash as the decimal cap.
     */
    maxIntegerDigits?: number;
    /**
     * External rejection trigger. Increment whenever a CONSUMER (e.g., a
     * swap hook with its own MAX_U64 cap) silently rejected a value the
     * input would otherwise have accepted. AmountInput fires the same
     * shake + warning flash it would for an internal cap rejection. Wire
     * to a counter exposed from the consumer hook.
     */
    rejectionTrigger?: number;
    /**
     * Replace the value display with an inline error message when set.
     * Use for read-only fields (`mode="display"`) where a quote/lookup
     * failed — e.g., "Quote unavailable" on the You Get input when
     * Jupiter has no route. Rendered with `text-error` styling.
     */
    errorMessage?: string;
}

/** Field height, pinned to each value variant's own line height. `fit`
 *  steps the font AND its leading down the ladder, so an auto-height field
 *  would shrink with the number; a fixed box keeps the row still. */
const VALUE_HEIGHT: Record<
    NonNullable<AmountInputProps["valueVariant"]>,
    string
> = {
    number: "h-5 md:h-6",
    "number-lg": "h-9 md:h-10"
};

/**
 * @version 2.0.0
 * @published 2026-04-10
 * @history
 *   2.0.0 — Breaking: removed `progress` and `maxButton`/`onMaxClick` props.
 *           Progress bar replaced by `<SeekBar>` via `seekable`/`pct`/`onSeek`/
 *           `focused` props. Max button removed — the 100% SeekBar tick is the
 *           max action. Added `readOnly` prop for display-only fields (e.g.,
 *           "You Get" in swap). `onChange` is now optional (omit for readOnly).
 *
 *   1.0.0 — Initial publish. Large numeric amount input used in the USDv swap
 *           and stake widgets. Two modes:
 *
 *           - **Progress mode** (`progress` prop): swap-style. Includes a thin
 *             ember progress bar with 0/25/50/75/100 tick marks that appear on
 *             focus and a click-to-seek interaction.
 *           - **Max button mode** (`maxButton` prop): stake-style. Includes a
 *             hover-revealed Max button that slides over the token chip.
 *
 *           The dollar prefix and token chip are both opt-in via props so the
 *           same component covers all four send/get/stake/unstake variants.
 */
export function AmountInput({
    valueVariant = "number-lg",
    fit = false,
    label,
    value,
    onChange,
    inputRef,
    prefix,
    tokenChip,
    placeholder = "0",
    onFocus,
    onBlur,
    balance,
    onBalanceClick,
    secondary,
    seekable = false,
    pct = 0,
    onSeek,
    mode = "input",
    focusBorder = false,
    metaCollapsed = false,
    loading = false,
    maxDecimals,
    maxIntegerDigits,
    rejectionTrigger,
    errorMessage
}: AmountInputProps): React.ReactElement {
    const caps = useInputCaps({
        tokenDecimals: maxDecimals,
        maxIntegerDigits
    });
    const parsed = parseFloat(value) || 0;
    const hasValue = parsed > 0;
    const isCompact = mode === "compact";
    const isReadOnly = mode === "display" || isCompact;
    const [isFocused, setIsFocused] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    // `rejectKey` increments whenever the sanitizer drops more than mere
    // non-numeric characters (i.e., a digit was rejected because it would
    // exceed the integer or decimal cap). The useEffect below restarts the
    // shake / warn-flash animations imperatively (without remounting the
    // input element, so focus + cursor position are preserved mid-typing).
    const [rejectKey, setRejectKey] = useState(0);
    const hasMeta = secondary !== undefined || balance;

    // Bridge: when a consumer's own cap rejects a keystroke (e.g., useSwap's
    // MAX_U64 check that returns early without updating state), the consumer
    // increments its `rejectionTrigger` counter. We listen and fire the
    // shake/flash here. First mount is skipped so we don't shake on initial
    // render when the consumer's counter starts at 0.
    const lastRejectionTriggerRef = useRef(rejectionTrigger ?? 0);
    useEffect(() => {
        if (rejectionTrigger === undefined) return;
        if (rejectionTrigger === lastRejectionTriggerRef.current) return;
        lastRejectionTriggerRef.current = rejectionTrigger;
        setRejectKey((k) => k + 1);
    }, [rejectionTrigger]);

    return (
        <div className="flex flex-col gap-2 relative">
            <div className="flex items-center">
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base"
                >
                    {label}
                </Text>
            </div>

            {/* Bordered input + meta wrapper — no flex gap between them so meta
                can animate its own margin-top down to 0 without a ghost gap. */}
            <div>
                {/* Bordered input area — contains input row + seekbar + focus border.
                Wrapped in <InputFeedback> so the shake + warn-border + input
                color flash animations fire on each rejected keystroke. */}
                <InputFeedback
                    trigger={rejectKey}
                    tone="warning"
                    className={`relative py-1 md:py-2 ${loading ? "animate-[pulse_1s_cubic-bezier(0.4,0,0.6,1)_infinite]" : ""}`}
                >
                    <div className="flex items-center px-2">
                        <div className="flex items-baseline flex-1 min-w-0 relative">
                            {prefix && (
                                <Text
                                    variant="number-lg"
                                    className={`${value ? "text-foreground-base" : "text-line-base"} ${errorMessage ? "invisible" : ""}`}
                                >
                                    {prefix}
                                </Text>
                            )}
                            {/* Design-system Input, not a raw <input>: its `fit`
                                steps the value down the type ladder (number-lg
                                36 -> 30 -> 24px) when it outgrows the field, then
                                scrolls. A Sep 10 merge swapped this for a raw
                                input with `fieldSizing: content`, which grows the
                                box instead, so long migration figures clipped
                                under the token chip. `bare` keeps the DS rail
                                off; `fit` defaults false, so swap and stake are
                                unchanged. */}
                            <DesignInput
                                bare
                                fit={fit}
                                textVariant={valueVariant}
                                ref={inputRef}
                                type="text"
                                inputMode="decimal"
                                pattern="[0-9]*\.?[0-9]*"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                                placeholder={placeholder}
                                value={value}
                                readOnly={isReadOnly}
                                tabIndex={isReadOnly ? -1 : undefined}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const sanitized = sanitizeDecimal(
                                        raw,
                                        caps
                                    );
                                    // Detect rejection: compare against the
                                    // bare strip-non-numerics version. If the
                                    // sanitizer dropped MORE than that, a digit
                                    // was capped — fire the rejection animation
                                    // (covers both integer and decimal caps).
                                    // Normalize commas → dots first so a Nordic
                                    // mobile keyboard (which exposes only `,`)
                                    // doesn't read as a rejection.
                                    const baseStripped = raw
                                        .replace(/,/g, ".")
                                        .replace(/[^0-9.]/g, "");
                                    if (sanitized !== baseStripped) {
                                        setRejectKey((k) => k + 1);
                                    }
                                    onChange?.(sanitized);
                                }}
                                onFocus={() => {
                                    if (isReadOnly) return;
                                    setHasInteracted(true);
                                    setIsFocused(true);
                                    onFocus?.();
                                }}
                                onBlur={() => {
                                    if (isReadOnly) return;
                                    setIsFocused(false);
                                    onBlur?.();
                                }}
                                className={`${VALUE_HEIGHT[valueVariant]} px-0 py-0 text-foreground-base placeholder-line-base bg-transparent outline-none flex-1 min-w-0 ${isReadOnly ? "cursor-default pointer-events-none" : "cursor-text"} ${errorMessage ? "invisible" : ""}`}
                            />
                            {errorMessage && (
                                // The input still owns the row's intrinsic height
                                // via its number-lg leading. We just hide its text
                                // and lay the error string over it as an absolute
                                // overlay — h1 size, vertically centered, truncates
                                // gracefully if the message is too long for the
                                // available width.
                                <Text
                                    variant="body"
                                    render={
                                        <span className="absolute inset-0 flex items-center" />
                                    }
                                    className="text-error truncate"
                                >
                                    {errorMessage}
                                </Text>
                            )}
                        </div>

                        {tokenChip && (
                            <div
                                className="shrink-0"
                                style={{
                                    marginLeft: isCompact ? "auto" : undefined
                                }}
                            >
                                {tokenChip}
                            </div>
                        )}
                    </div>

                    {/* SeekBar fades in compact mode */}
                    {seekable && (
                        <div
                            className="mt-1 md:mt-3"
                            style={{
                                opacity: isCompact ? 0 : 1,
                                transition: "opacity 200ms ease-out"
                            }}
                        >
                            <SeekBar
                                pct={pct}
                                focused={isFocused}
                                onSeek={onSeek}
                            />
                        </div>
                    )}

                    <InputUnderline
                        variant={
                            seekable
                                ? "none"
                                : focusBorder
                                  ? "focus-border"
                                  : "focus-line"
                        }
                        focused={isFocused}
                        hasInteracted={hasInteracted}
                    />
                </InputFeedback>

                {/* Meta row — outside the bordered area. Animates its own
                max-height + opacity + margin-top to 0 when `metaCollapsed`
                is true (or compact mode), so no ghost gap remains. */}
                {hasMeta && (
                    <div
                        className="flex items-center justify-between gap-3 text-foreground-neutral px-1 overflow-hidden mt-1 md:mt-2"
                        style={{
                            maxHeight: metaCollapsed || isCompact ? 0 : 30,
                            opacity: metaCollapsed || isCompact ? 0 : 1,
                            // mt-1 md:mt-2 (4px / 8px) sets the expanded
                            // spacing via className. Inline `margin-top: 0`
                            // overrides only when collapsed so the className
                            // wins in the expanded state and the transition
                            // animates between them.
                            ...(metaCollapsed || isCompact
                                ? { marginTop: 0 }
                                : {}),
                            transition:
                                "max-height 300ms ease-out, opacity 200ms ease-out 100ms, margin-top 300ms ease-out"
                        }}
                    >
                        {secondary !== undefined && (
                            <Text
                                variant="small"
                                render={<span />}
                                className={`truncate min-w-0 transition-opacity duration-[400ms] ${hasValue ? "opacity-100" : "opacity-0"}`}
                            >
                                {secondary}
                            </Text>
                        )}
                        {balance &&
                            ((): JSX.Element => {
                                const handleBalanceClick =
                                    onBalanceClick ??
                                    (seekable && onSeek
                                        ? (): void => onSeek(100)
                                        : undefined);
                                const canClickMax =
                                    !!handleBalanceClick && !isReadOnly;
                                return (
                                    <Text
                                        variant="small"
                                        render={
                                            canClickMax ? (
                                                <button
                                                    type="button"
                                                    onMouseDown={(event) =>
                                                        event.preventDefault()
                                                    }
                                                    onClick={handleBalanceClick}
                                                />
                                            ) : (
                                                <span />
                                            )
                                        }
                                        className={`ml-auto whitespace-nowrap shrink-0${
                                            canClickMax
                                                ? " cursor-pointer hover:text-foreground-base transition-colors duration-200 ease-[var(--ease-out-quad)] underline-offset-2 hover:underline"
                                                : ""
                                        }`}
                                    >
                                        Balance: {balance}
                                    </Text>
                                );
                            })()}
                    </div>
                )}
            </div>
        </div>
    );
}
